import { clamp, omit } from 'lodash';
import { create } from 'zustand';
import { KnownClientId, rootModuleId } from '@app/const';
import { Design, DesignModule } from '@app/types';
// This import cannot be shortened without creating circular dependencies.
import { getLocalStorage, setLocalStorage } from '@app/utilities/functions/local-storage';
// This import cannot be shortened without creating circular dependencies.
import { pick } from '@app/utilities/functions/object';

const canvasTabsKey = 'savedTabs';
let lastWrittenSavedTabs: string | null = null;

type CanvasTab = {
  /** Id (matches @see DesignModule.id). */
  id: string;
  /** Whether the @see DesignModule associated with this tab is a common module. */
  isCommonModule: boolean;
  /** Label of the tab (matches @see DesignModule.name). */
  label: string;
};

function createTab(
  { id, props, name }: DesignModule,
  { clientId }: Design
): CanvasTab {
  return {
    id,
    isCommonModule: (
      !!props.isCommonModule
      || clientId === KnownClientId.interactionsCommon
    ),
    label: name
  };
}

function modulesById(
  modules: readonly DesignModule[]
): Map<string, DesignModule> {
  return new Map(
    modules.map((mod) => [mod.id, mod])
  );
}

function tabsMatchSerialized(
  tabs: readonly CanvasTab[],
  serialized: Design
): boolean {
  const moduleMap = modulesById(serialized.components.modules);
  return tabs.every((tab) => {
    const mod = moduleMap.get(tab.id);
    if (!mod) {
      return false;
    }

    const next = createTab(mod, serialized);
    return (
      next.isCommonModule === tab.isCommonModule
      && next.label === tab.label
    );
  });
}

function cleanState(
  {
    activeTabId,
    tabs,
    tabsToRerender
  }: Omit<CanvasTabsState, 'initialized'>,
  serialized: Design,
  oldState?: Omit<CanvasTabsState, 'initialized'>
): Omit<CanvasTabsState, 'initialized'> {
  const { components: { modules } } = serialized;

  // Remove tabs without modules.
  const moduleMap = modulesById(modules);
  tabs = (
    tabs
    .map((tab): CanvasTab | null => {
      const mod = moduleMap.get(tab.id);
      // Refresh tab in case name changes from one branch/version to another.
      return (!mod) ? null : createTab(mod, serialized);
    })
    .filter((tab) => tab != null)
  );

  // Make sure @see rootModuleId is first.
  if (tabs[0]?.id !== rootModuleId) {
    const rootModule = moduleMap.get(rootModuleId);
    if (!rootModule) {
      throw new Error(`design.json files are required to use '${rootModuleId}' as their root module.`);
    }

    tabs = [
      createTab(rootModule, serialized),
      // This filter should not ever find anything, but it is here just in case.
      ...tabs.filter((tab) => tab.id !== rootModuleId)
    ];
  }

  const openTabIds = new Set(tabs.map((tab) => tab.id));
  if (!openTabIds.has(activeTabId)) {
    if (!oldState) {
      // Restoring: a stored id naming a module that is gone falls back to root.
      activeTabId = rootModuleId;
    } else {
      // Prefer the first still-open tab that sat to the right of the closed
      // active tab; fall back to the last remaining tab.
      const closedActiveIndex = oldState.tabs.findIndex((tab) => {
        return tab.id === oldState.activeTabId;
      });
      // Still-open tabs at or left of the closed active; length is the index of
      // the first survivor that was to its right.
      const preferredNextIndex = (
        oldState.tabs
        .slice(0, Math.max(closedActiveIndex, 0))
        .filter(({ id }) => openTabIds.has(id))
        .length
      );
      activeTabId = (tabs[preferredNextIndex] ?? tabs[tabs.length - 1]).id;
    }
  }

  // Drop flags for closed tabs and any explicit false entries.
  tabsToRerender = Object.fromEntries(
    Object.entries(tabsToRerender).filter(([key, rerender]) => {
      return rerender && openTabIds.has(key);
    })
  );

  return {
    activeTabId,
    tabs,
    tabsToRerender
  };
}

/** Current persist shape. */
type StoredDesignTabs = Pick<CanvasTabsState, 'activeTabId' | 'tabs'>;

/** @deprecated Legacy state used before upgrading tabs to React. */
type LegacyStoredDesignTabs = {
  /** Matches @see CanvasTab.id. */
  id: string;
  /** Renamed to @see CanvasTab.label. */
  text: string;
}[];

type CanvasTabsStateForAllDesigns = Record<
  string,
  Record<string, StoredDesignTabs | undefined> | undefined
>;

function getLocalStorageState(
  serialized: Design
): Omit<CanvasTabsState, 'initialized'> {
  const { application, client } = serialized;
  const stateForAllDesigns = getLocalStorage<CanvasTabsStateForAllDesigns>(canvasTabsKey) ?? {};
  const stored = stateForAllDesigns[client]?.[application] as (
    StoredDesignTabs
    | LegacyStoredDesignTabs
    | undefined
  );

  const {
    activeTabId = rootModuleId,
    tabs = []
  } = (!(stored instanceof Array)) ? (stored ?? {}) : {
    // Convert legacy state stored as a bare array of tabs.
    // Let @see cleanState refresh these from their modules & drop tabs without mods.
    tabs: stored.map(({ id, text }) => {
      return {
        id,
        isCommonModule: false,
        label: text
      };
    })
  };

  return {
    activeTabId,
    tabs,
    tabsToRerender: {}
  };
}

function setLocalStorageState(
  state: Omit<CanvasTabsState, 'initialized'>,
  serialized: Design
): void {
  const { application, client } = serialized;
  const stateForAllDesigns = getLocalStorage<CanvasTabsStateForAllDesigns>(canvasTabsKey) ?? {};
  const stateToSave = pick(state, ['activeTabId', 'tabs']);
  stateToSave.tabs = stateToSave.tabs.filter((tab) => tab.id !== rootModuleId);

  stateForAllDesigns[client] ??= {};
  stateForAllDesigns[client][application] = stateToSave;
  setLocalStorage(canvasTabsKey, stateForAllDesigns);
  lastWrittenSavedTabs = localStorage.getItem(canvasTabsKey);
}

export type CanvasTabsState = {
  /** Tab currently displayed on the canvas page. */
  activeTabId: string;
  initialized: boolean;
  /** Tabs for modules that are currently open. */
  tabs: readonly CanvasTab[];
  /** IDs of modules that need their flow rerendered are set to true. */
  tabsToRerender: Record<string, boolean>;
};

type CanvasTabsActions = {
  addTab: (id: string, options?: { activate?: boolean; }) => void;
  /** Clears all tabs except for the @see rootModuleId & the provided @see id. */
  closeOtherTabs: (id: string) => void;
  closeTab: (id: string) => void;
  /** Closes every tab except @see rootModuleId when @see ids is omitted. */
  closeTabs: (ids?: string[]) => void;
  closeTabsToLeft: (id: string) => void;
  closeTabsToRight: (id: string) => void;
  initTabs: (serialized: Design) => void;
  isTabOpen: (id: string) => boolean;
  /** Moves the tab for @see id to @see toIndex, keeping @see rootModuleId first. */
  moveTab: (id: string, toIndex: number) => void;
  /** Labels are read from the design, so this only triggers an update for @see id. */
  renameTab: (id: string) => void;
  rerenderTab: (id: string) => void;
  rerenderTabs: () => void;
  setActiveTab: (id: string) => void;
  tabRerendered: (id: string) => void;
};

type CanvasTabsStore = CanvasTabsState & CanvasTabsActions;

export const useCanvasTabs = create<CanvasTabsStore>((
  setState,
  getState
): CanvasTabsStore => {
  let serialized: Design | null = null;

  const setAndPersistState = (
    updates: Partial<CanvasTabsState>,
    { isInit = false }: { isInit?: boolean; } = {}
  ) => {
    // Calls before initialization should not happen.
    if (!serialized) {
      return;
    }

    const oldState = pick(getState(), ['activeTabId', 'tabs', 'tabsToRerender']);
    const newState = cleanState(
      { ...oldState, ...updates },
      serialized,
      // Successor selection in @see cleanState is meaningless when restoring.
      (isInit) ? undefined : oldState
    );

    setLocalStorageState(newState, serialized);
    setState(newState);
  };

  return {
    activeTabId: '',
    addTab: (id, { activate = true } = {}) => {
      if (!serialized) {
        return;
      }
      const { setActiveTab, tabs } = getState();
      const mod = modulesById(serialized.components.modules).get(id);
      if (!mod) {
        return;
      } else if (tabs.some((tab) => tab.id === id)) {
        if (activate) {
          setActiveTab(id);
        }
        return;
      }
      const newState: Partial<CanvasTabsState> = {
        tabs: [...tabs, createTab(mod, serialized)]
      };
      if (activate) {
        newState.activeTabId = id;
      }
      setAndPersistState(newState);
    },
    closeOtherTabs: (id) => {
      const { closeTabs, tabs } = getState();
      closeTabs(
        tabs
        .map((tab) => tab.id)
        .filter((tabId) => tabId !== rootModuleId && tabId !== id)
      );
    },
    closeTab: (id) => {
      getState().closeTabs([id]);
    },
    closeTabs: (ids) => {
      const oldTabs = getState().tabs;
      const idsToClose = (ids == null) ? null : new Set(ids);
      const tabs = oldTabs.filter((tab) => {
        return (
          tab.id === rootModuleId
          || (idsToClose != null && !idsToClose.has(tab.id))
        );
      });
      if (tabs.length !== oldTabs.length) {
        setAndPersistState({ tabs });
      }
    },
    closeTabsToLeft: (id) => {
      const { closeTabs, tabs } = getState();
      const tabI = tabs.findIndex((tab) => tab.id === id);
      if (tabI < 0) {
        return;
      }
      closeTabs(
        tabs
        .map((tab) => tab.id)
        // Keep 0 since it is @see rootModuleId.
        .filter((_, i) => i > 0 && i < tabI)
      );
    },
    closeTabsToRight: (id) => {
      const { closeTabs, tabs } = getState();
      const tabI = tabs.findIndex((tab) => tab.id === id);
      if (tabI < 0) {
        return;
      }
      closeTabs(
        tabs
        .map((tab) => tab.id)
        .filter((_, i) => i > tabI)
      );
    },
    initialized: false,
    initTabs: (newSerialized) => {
      const previous = serialized;
      serialized = newSerialized;
      if (
        getState().initialized
        && previous != null
        && previous.application === newSerialized.application
        && previous.client === newSerialized.client
        && localStorage.getItem(canvasTabsKey) === lastWrittenSavedTabs
        && tabsMatchSerialized(getState().tabs, newSerialized)
      ) {
        return;
      }

      setAndPersistState(getLocalStorageState(serialized), { isInit: true });
      if (!getState().initialized) {
        setState({ initialized: true });
      }
    },
    isTabOpen: (id) => getState().tabs.some((tab) => tab.id === id),
    moveTab: (id, toIndex) => {
      const tabs = [...getState().tabs];
      const fromIndex = tabs.findIndex((tab) => tab.id === id);
      // Cannot move the @see rootModuleId tab (index 0) or one that is not open.
      if (fromIndex <= 0) {
        return;
      }
      // Cannot move to index 0 since @see rootModuleId tab is there.
      toIndex = clamp(toIndex, 1, tabs.length - 1);
      const [tabToMove] = tabs.splice(fromIndex, 1);
      tabs.splice(toIndex, 0, tabToMove);
      setAndPersistState({ tabs });
    },
    renameTab: (id) => {
      const { tabs } = getState();
      if (id === rootModuleId || !tabs.some((tab) => tab.id === id)) {
        return;
      }
      // Empty update: @see cleanState rebuilds labels from the design modules.
      setAndPersistState({});
    },
    rerenderTab: (id) => {
      const { tabsToRerender } = getState();
      setState({ tabsToRerender: { ...tabsToRerender, [id]: true } });
    },
    rerenderTabs: () => {
      const { tabs } = getState();
      const tabsToRerender = Object.fromEntries(tabs.map(({ id }) => [id, true]));
      setState({ tabsToRerender });
    },
    setActiveTab: (id) => {
      const { tabs } = getState();
      if (tabs.some((tab) => tab.id === id)) {
        setAndPersistState({ activeTabId: id });
      }
    },
    tabRerendered: (id) => {
      const { tabsToRerender } = getState();
      setState({ tabsToRerender: omit(tabsToRerender, id) });
    },
    tabs: [],
    tabsToRerender: {}
  };
});
